import { createSlice } from '@reduxjs/toolkit';
import { filter, includes, isEqual, map, orderBy } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';
import { checkFilterMatch } from '../../utils/filters';

// URLSearchParams
const initialArgs = {
  date: moment().format('YYYY-MM-DD'),
  anomalies: false
};
const { date, siteId, anomalies } = proc.getQueryParams(initialArgs);

const initialState = {
  isLoading: false,
  error: false,
  refreshFilters: false,
  // URLSearchParams
  date,
  isPublished: false,
  pickerDate: moment(date, 'YYYY-MM-DD').toDate(),
  siteId,
  // shifts
  services: [],
  // api call props
  getServicesProps: null,
  // sort
  sortBy: ['datetime_start'],
  sortOrders: ['asc'],
  // sortBy: 'datetime_start',
  // sortOrders: 'asc',
  // filter
  keyToAvoid: null,
  filterOptionsDatas: {
    sites: [],
    profiles: [],
    activities: [],
    prestations: []
  },
  filterOptions: {
    sites: [],
    profiles: [],
    activities: [],
    prestations: []
  },
  filters: {
    search: '',
    sites: [],
    profiles: [],
    activities: [],
    prestations: [],
    unassigned: false,
    inProgress: false,
    finished: false,
    previousVacs: false,
    anomalies
  }
};

const slice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getServices(state, action) {
      const { props, shifts, sites, profiles, activities, prestations, isPublished } = action.payload;

      state.getServicesProps = props;
      state.pickerDate = moment(props.date, 'YYYY-MM-DD').toDate();
      state.services = map(shifts, (el) => {
        if (el.pds_status === null && !el.contract_id) {
          el.pds_status = 3;
        }
        if (el.pds_status === null && el.contract_id) {
          el.pds_status = 4;
        }
        if (el.fds_status === null && !el.contract_id) {
          el.fds_status = 3;
        }
        if (el.fds_status === null && el.contract_id) {
          el.fds_status = 4;
        }
        return el;
      });

      state.filterOptionsDatas.sites = sites;
      state.filterOptionsDatas.profiles = profiles;
      state.filterOptionsDatas.activities = activities;
      state.filterOptionsDatas.prestations = prestations;
      state.filterOptions.sites = sites;
      state.filterOptions.profiles = profiles;
      state.filterOptions.activities = activities;
      state.filterOptions.prestations = prestations;
      state.refreshFilters = !state.refreshFilters;
      // suite au CW-2176 => on enlève seulement si pas dans les résultats...
      // Object.keys(state.filters).forEach((key) => {
      //   if (!['search', 'anomalies', 'finished', 'inProgress', 'unassigned', 'previousVacs'].includes(key)) {
      //     // si on a des filtres sélectionnés
      //     if (state.filters[key] && state.filters[key].length) {
      //       // on retire les données superflues => on ressort les ids des données reçue en requête
      //       const dataResult = [...action.payload[key]].map((el) => el.id);
      //       // on met à jour
      //       state.filters[key] = state.filters[key].filter((data) => dataResult.includes(data));
      //     }
      //   }
      // });

      const { siteId } = proc.getQueryParams(initialArgs);
      state.siteId = siteId;
      state.isPublished = isPublished;
      state.isLoading = false;
    },

    setDateServices(state, action) {
      state.pickerDate = action.payload;
      state.date = moment(state.pickerDate).format('YYYY-MM-DD');
    },

    setService(state, action) {
      const service = action.payload;
      const idx = state.services.findIndex((el) => el.id === service.id);
      if (idx >= 0) {
        state.services[idx] = { ...state.services[idx], ...service };
      }
    },
    setFilterToAvoid(state, action) {
      state.keyToAvoid = action.payload;
    },

    // SORT
    sortByServices(state, action) {
      // state.sortBy = action.payload;
      // if (state.sortOrders === 'desc') state.sortOrders = 'asc';
      // else state.sortOrders = 'desc';
      const idx = state.sortBy.findIndex((el) => el === action.payload);
      if (idx >= 0) {
        if (state.sortOrders[0] === 'desc') {
          state.sortBy.splice(0, 1);
          state.sortOrders.splice(0, 1);
        } else {
          state.sortOrders[0] = 'desc';
        }
      } else {
        state.sortBy[0] = action.payload;
        state.sortOrders[0] = 'asc';
      }
    },

    // FILTER
    filterServices(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    updateFilters(state, action) {
      state.filterOptions = action.payload;
    },

    // RESET FILTER
    resetFilterServices(state, action) {
      state.filters = initialState.filters;
      action.payload = initialState.filters;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setDateServices,
  setService,
  setFilterToAvoid,
  sortByServices,
  filterServices,
  updateFilters,
  resetFilterServices
} = slice.actions;

export function getServices(props, reload = false) {
  const { date, hasServices, siteId = null } = props;

  return async (dispatch, getState) => {
    const { getServicesProps } = getState().services;

    if (reload || !isEqual(props, getServicesProps)) {
      dispatch(slice.actions.startLoading());
    }

    try {
      const result = await socket.get('/services', {
        date,
        siteId,
        hasServices
      });
      dispatch(slice.actions.getServices({ ...result, props }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function applyFilter(
  shifts,
  filters,
  headCols,
  date = null,
  clockingsEnabled = false,
  sortBy = initialState.sortBy,
  sortOrders = initialState.sortOrders
) {
  // FILTER
  if (filters.sites.length > 0) {
    const ids = map(filters.sites, (el) => el.id);
    shifts = filter(shifts, (shift) => includes(ids, shift.site_id));
  }
  if (filters.profiles.length > 0) {
    const ids = map(filters.profiles, (el) => el.id);
    shifts = filter(shifts, (shift) => includes(ids, shift.profile_id));
  }
  if (filters.activities.length > 0) {
    const ids = map(filters.activities, (el) => el.id);
    shifts = filter(shifts, (shift) => includes(ids, shift.activity_id));
  }
  if (filters.prestations.length > 0) {
    const ids = map(filters.prestations, (el) => el.id);
    shifts = filter(shifts, (shift) => includes(ids, shift.prestation_id));
  }

  if (!filters.unassigned) {
    shifts = filter(shifts, (shift) => shift.contract_id !== null);
  }

  if (filters.inProgress || filters.finished || filters.anomalies) {
    shifts = filter(shifts, (shift) => {
      const started = moment().isSameOrAfter(shift.datetime_start);
      const isPreClockingAnomaly =
        shift.pre_clocking_enabled &&
        !shift.pre_pds_datetime &&
        moment().isSameOrAfter(
          moment(shift.datetime_start).subtract('minutes', shift.pre_clocking_resource_notification_minutes)
        );

      const vacIsOver = moment(shift.datetime_end).isSameOrBefore();
      const inProgress = started && !vacIsOver && shift.datetime_arrived_at;
      const finished = started && shift.datetime_leaved_at && moment(shift.datetime_end).isSameOrBefore();
      const anomalies =
        ((started && (!shift.datetime_arrived_at || shift.pds_status === 2)) || isPreClockingAnomaly) &&
        shift.contract_id &&
        shift.pds_status !== null;
      const geolocProblem =
        shift.pds_is_forced || shift.fds_is_forced || shift.fds_is_outside_area || shift.pds_is_outside_area;
      if (clockingsEnabled) {
        return (
          (filters.inProgress && inProgress) ||
          (filters.finished && finished) ||
          (filters.anomalies && anomalies) ||
          (filters.anomalies && geolocProblem)
        );
      }
      return (filters.inProgress && inProgress) || (filters.finished && finished) || (filters.anomalies && anomalies);
    });
  }

  if (date && !filters.previousVacs) {
    shifts = filter(shifts, (shift) => moment(date).isSame(shift.datetime_start, 'day'));
  }

  // FILTER
  if (filters.search) {
    shifts = filter(shifts, (shift) => {
      for (let idx = 0; idx < headCols.length; idx += 1) {
        if (checkFilterMatch(headCols, idx, shift, filters)) {
          return true;
        }
      }
      return false;
    });
  }

  // SORT
  shifts = orderBy(shifts, sortBy, sortOrders);

  return shifts;
}
